// @flow

import * as React from 'react';
import { Environment } from '@ta/lithium.context';
import AttachedOverlay, { WindowPadding, type Edge } from '@ta/overlays.attached-overlay';
import Shift from '@ta/overlays.shift';
import { Arrow, ArrowSize, ShadowBox, SmallArrowSize, type Side } from '@ta/overlays.pieces';

/** The type of arrow to use */
export type ArrowType = 'none' | 'small' | 'large';

type Props = {|
  /**  The element to attach this overlay to. */
  target: HTMLElement,

  /** The position relative to the target element */
  position: 'above' | 'below' | 'rightOf' | 'leftOf',

  /** The edge to which you want to align the target and the overlay. Defaults to being centered. */
  alignEdge?: Edge,

  /** True if it is fixed position, false if it is absolute. Defaults to false/absolute. */
  fixed?: boolean,

  /**
   * The number of pixels to shift down. If position is 'below' it will put enough top offset for the arrow by default.
   */
  shiftX?: number,

  /**
   * The number of pixels to shift up. If position is 'above' it will put enough bottom offset for the arrow by
   * default.
   */
  shiftY?: number,

  /**
   * The number of pixels to shift the arrow, in whichever dimension it will be. Only used if the arrow is not centered.
   */
  shiftArrow?: number,

  /** Whether the arrow is small, large, or nonexistant. Defaults to 'large' */
  arrowType?: ArrowType,

  /** If a small arrow should be used */
  small?: boolean,

  /** Whether the arrow is inverted or not */
  inverted?: boolean,

  /** Child react nodes */
  children: React.Node,

  /** True if the overlay should animate position changes */
  animated?: boolean,

  /** @deprecated Choice determines the z-index of the overlay */
  overlayType?: 'modal' | 'background' | 'tooltip' | 'popover',

  /** When this overlay closes, return focus to the target element */
  shouldRestoreFocus?: boolean,
|};

/** Function return position of overlay in dependence with target  */
export const getPosition = (isRtl: boolean, position: any) => {
  if (isRtl) {
    if (position === 'rightOf') {
      return 'leftOf';
    }
    if (position === 'leftOf') {
      return 'rightOf';
    }
  }
  return position;
};

/** Function return alignment edge of overlay in dependence with target */
export const getAlignEdge = (isRtl: boolean, alignEdge: any) => {
  if (isRtl) {
    if (alignEdge === 'right') {
      return 'left';
    }
    if (alignEdge === 'left') {
      return 'right';
    }
  }
  return alignEdge;
};

/** Function return arrow offset */
export const getArrowOffset = (isRtl: boolean, shiftArrow: number, alignEdge: any, target: any) => {
  // Special case windowX alignment to align the arrow in the middle of the target
  if (alignEdge === 'windowX') {
    const rect = target.getBoundingClientRect();
    if (rect) {
      const multiplier = isRtl ? -1 : 1;
      return (rect.width / 2 + rect.left - 2 * WindowPadding) * multiplier;
    }
  }
  return shiftArrow;
};

const getArrowSize = (arrow: ArrowType): number => {
  if (arrow === 'small') {
    return SmallArrowSize;
  }
  if (arrow === 'none') {
    return 0;
  }
  return ArrowSize;
};

/**
 * An overlay attached to a target element with an arrow. Note that this component does not perscribe how the overlay
 * is shown or dismissed and so you will most likely use it in the context of <Global/> with
 * ta/state/overlays/showGlobalOverlay().
 *
 * This defaults to an overlay below the target with an arrow left-aligned on the top of the overlay.
 */
const AttachedArrowOverlay = (props: Props) => {
  const {
    locale: { isRtl },
  } = React.useContext(Environment);
  const position = getPosition(isRtl, props.position);
  const alignEdge = getAlignEdge(isRtl, props.alignEdge);
  const { arrowType = 'large' } = props;
  const { shiftArrow = 0, target, shouldRestoreFocus } = props;
  const targetRef = React.useRef();
  let { shiftX = 0, shiftY = 0 } = props;
  shiftX *= isRtl ? -1 : 1;
  const arrowSize = getArrowSize(arrowType);
  let arrowOffset = getArrowOffset(isRtl, shiftArrow, props.alignEdge, props.target);
  let arrowOn: Side;
  switch (position) {
    case 'above': {
      arrowOn = 'bottom';
      arrowOffset *= isRtl ? -1 : 1;
      arrowOffset -= shiftX;
      shiftY -= arrowSize;
      break;
    }
    case 'below': {
      arrowOn = 'top';
      arrowOffset *= isRtl ? -1 : 1;
      arrowOffset -= shiftX;
      shiftY += arrowSize;
      break;
    }
    case 'rightOf': {
      arrowOn = isRtl ? 'right' : 'left';
      arrowOffset -= shiftY;
      shiftX += arrowSize;
      break;
    }
    default: {
      // leftOf
      arrowOn = isRtl ? 'left' : 'right';
      arrowOffset -= shiftY;
      shiftX -= arrowSize;
      break;
    }
  }

  arrowOffset *= alignEdge === 'bottom' || alignEdge === 'right' ? -1 : 1;

  React.useEffect(() => {
    targetRef.current = target;
  }, [target]);

  React.useEffect(
    () => () => {
      if (shouldRestoreFocus && targetRef && targetRef.current) {
        targetRef.current.focus();
      }
    },
    [targetRef, shouldRestoreFocus],
  );

  return (
    <AttachedOverlay
      target={props.target}
      position={position}
      alignEdge={alignEdge}
      fixed={props.fixed}
      animated={props.animated}
      overlayType={props.overlayType}
    >
      <Shift x={shiftX} y={shiftY}>
        <ShadowBox inverted={props.inverted}>
          {arrowType !== 'none' && (
            <Arrow
              on={arrowOn}
              from={alignEdge === 'windowX' ? 'left' : alignEdge}
              offset={arrowOffset}
              small={arrowType === 'small'}
              inverted={props.inverted}
            />
          )}
          {props.children}
        </ShadowBox>
      </Shift>
    </AttachedOverlay>
  );
};

AttachedArrowOverlay.defaultProps = {
  alignEdge: undefined,
  fixed: false,
  shiftX: 0,
  shiftY: 0,
  shiftArrow: 0,
  arrowType: 'large',
  inverted: false,
  animated: false,
  overlayType: 'modal',
};

export default AttachedArrowOverlay;
